import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'

import {
  type ClassroomPostAudience,
  isTheWholeClassroom,
  mayAnyoneRead,
} from '#/state/classrooms/types'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {postAudienceName} from '#/components/classrooms/postAudienceNames'
import {TinyChevronBottom_Stroke2_Corner0_Rounded as TinyChevronIcon} from '#/components/icons/Chevron'
import {Earth_Stroke2_Corner0_Rounded as EarthIcon} from '#/components/icons/Globe'
import {Group3_Stroke2_Corner0_Rounded as GroupIcon} from '#/components/icons/Group'
import {Person_Stroke2_Corner0_Rounded as PersonIcon} from '#/components/icons/Person'

/**
 * Who this post is for, said on the pill that changes it.
 *
 * The second pill in a classroom's composer, and the one that exists only there:
 * outside a room a post has one audience and no way to choose another, so there is
 * nothing to say and no pill to say it with. It is drawn beside *Anyone can
 * interact* rather than inside it, because the two are different questions about
 * the same post — who may see it, and who may answer it — and a person looking for
 * *who can see this* should find a control that names it rather than one that hides
 * it behind a reply settings dialog.
 *
 * The pill's own words are the answer, not the question, for the same reason the
 * interaction pill's are: a teacher about to publish an exam paper should be able
 * to see, without opening anything, that the post is not going to the world. What
 * the answer *is* comes from one place (`postAudienceName`), so the pill, the
 * dialog and the card a stranger is shown cannot call it three different things.
 *
 * It opens the audience's own dialog (`PostAudienceDialog`), which is handed in as
 * `onPress` — the same relationship the interaction pill has with its dialog, and
 * the two dialogs are told apart by their doors: a person who pressed *Anyone can
 * interact* is choosing who may answer, and a person who pressed this is choosing
 * who may read.
 */
export function PostAudienceBtn({
  audience,
  onPress,
}: {
  audience: ClassroomPostAudience
  onPress: () => void
}) {
  const {_} = useLingui()
  const label = postAudienceName(_, audience)

  /*
   * One icon per kind of answer: the world for the world, the room for the room,
   * and a person for the seats — because at a glance on a phone the shape is what
   * is read first, and the three shapes have to mean the three answers.
   *
   * The two *everybody* answers are read off the audience the way the rules read
   * them (`mayAnyoneRead`, `isTheWholeClassroom`) rather than off its length, so
   * the pill draws the same answer the dialog and the card are speaking about.
   */
  const Icon = mayAnyoneRead(audience)
    ? EarthIcon
    : isTheWholeClassroom(audience)
      ? GroupIcon
      : PersonIcon

  return (
    <Button
      color="secondary"
      size="small"
      testID="openPostAudienceButton"
      onPress={onPress}
      label={label}
      accessibilityHint={_(
        msg`Opens a dialog to choose who can see this post`,
      )}>
      <ButtonIcon icon={Icon} />
      <ButtonText numberOfLines={1} maxFontSizeMultiplier={2}>
        {label}
      </ButtonText>
      <ButtonIcon icon={TinyChevronIcon} size="2xs" />
    </Button>
  )
}
